import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Loader, Button, SectionCard } from "@/components/PageUI";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { LineChart as LineIcon } from "lucide-react";

export default function Performance() {
  const { sportMeta } = useAuth();
  const cats = sportMeta?.rating_categories || ["Skill", "Speed", "Passing", "Shooting", "Defense", "Fitness", "Discipline", "Leadership", "Teamwork", "Coachability"];
  const [players, setPlayers] = useState([]);
  const [playerId, setPlayerId] = useState("");
  const [scores, setScores] = useState({});
  const [note, setNote] = useState("");
  const [summary, setSummary] = useState({ averages: {}, overall: 0, count: 0 });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/players").then((r) => { setPlayers(r.data); if (r.data[0]) setPlayerId(r.data[0].id); });
  }, []);

  useEffect(() => {
    if (playerId) api.get(`/ratings/summary?player_id=${playerId}`).then((r) => setSummary(r.data));
    setScores({});
  }, [playerId]);

  const save = async () => {
    const categories = {};
    cats.forEach((c) => { categories[c] = scores[c] ?? 5; });
    setLoading(true);
    try {
      await api.post("/ratings", { player_id: playerId, categories, note });
      toast.success("Rating saved");
      const r = await api.get(`/ratings/summary?player_id=${playerId}`); setSummary(r.data);
      setNote("");
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  if (!players) return <Loader />;

  return (
    <div className="animate-fade-up">
      <PageHeader title="Performance Ratings" subtitle={`Rate athletes across ${cats.length} sport-specific skills. Weekly & monthly progress.`} testid="performance-header" />
      {players.length === 0 ? (
        <EmptyState icon={LineIcon} title="No athletes yet" desc="Add athletes to rate their performance." testid="empty-performance" />
      ) : (
        <div className="grid lg:grid-cols-2 gap-4">
          <SectionCard title="New Evaluation" testid="rating-form">
            <div className="mb-4">
              <Select value={playerId} onValueChange={setPlayerId}>
                <SelectTrigger data-testid="performance-player-select"><SelectValue /></SelectTrigger>
                <SelectContent>{players.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-4">
              {cats.map((c) => (
                <div key={c}>
                  <div className="flex justify-between text-sm mb-1"><span>{c}</span><span className="font-semibold text-primary">{scores[c] ?? 5}</span></div>
                  <input type="range" min="1" max="10" value={scores[c] ?? 5} onChange={(e) => setScores((s) => ({ ...s, [c]: +e.target.value }))}
                    className="w-full accent-[#007AFF]" data-testid={`rating-slider-${c}`} />
                </div>
              ))}
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="Coach notes..." className="mt-4" data-testid="rating-note" />
            <Button onClick={save} disabled={loading} className="mt-4 w-full brand-gradient text-white border-0 hover:opacity-90" data-testid="save-rating-btn">{loading ? "Saving..." : "Save evaluation"}</Button>
          </SectionCard>

          <SectionCard title={`Averages · ${summary.count} evaluations`} testid="rating-summary">
            <div className="text-center mb-6">
              <p className="font-heading font-extrabold text-5xl brand-gradient-text">{summary.overall || "—"}</p>
              <p className="text-sm text-muted-foreground">Overall rating /10</p>
            </div>
            <div className="space-y-3">
              {Object.entries(summary.averages).map(([k, v]) => (
                <div key={k}>
                  <div className="flex justify-between text-sm mb-1"><span>{k}</span><span className="font-semibold">{v}</span></div>
                  <div className="h-2 rounded-full bg-accent overflow-hidden"><div className="h-full brand-gradient" style={{ width: `${v * 10}%` }} /></div>
                </div>
              ))}
              {!Object.keys(summary.averages).length && <p className="text-sm text-muted-foreground text-center py-6">No evaluations yet.</p>}
            </div>
          </SectionCard>
        </div>
      )}
    </div>
  );
}
